import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { ROUTES_ADMIN } from '@/router'
import {
  QUERIES_ADMIN,
  useSupplierOrderDetail,
  useSupplierOrderEdit,
} from '@/services'
import { Button, EditIcon, showToast, Text } from '@mercai/clever'

import { Tabs, TabsItemProps } from '@/components'
import { useParamsState } from '@/helpers'

import { TabDocuments } from './tab-documents'
import { TabGeneral } from './tab-general'
import { TabPayments } from './tab-payments'
import { TabProducts } from './tab-products'

import {
  SupplyOrderStatusCancelledReasonEnum,
  UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum,
} from '@/types'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ModalSupplyOrderCancellationReason } from '../../components'
import { SelectSupplyOrderStatus } from './components'

import styles from './supply-order-detail.module.css'
import { TabComments } from './tab-comments'
import { TabHistory } from './tab-history'

export const SupplyOrderDetail = () => {
  const { t } = useTranslation('screenSupplyOrderDetail')

  const { uuid } = useParams<{ uuid: string }>()

  const queryClient = useQueryClient()

  const [params, setParams] = useParamsState({
    tab: 'general',
  })

  const [isOpenModalCancellation, setIsOpenModalCancellation] = useState(false)

  const { data, isLoading } = useSupplierOrderDetail(uuid)

  const { mutateAsync } = useSupplierOrderEdit(uuid)

  const onChangeTab = (tab: string) => {
    setParams({ ...params, tab })
  }

  const onCancelSupplyOrder = async (values: {
    action?: keyof typeof UpdateSupplyOrderOnSaleOrderCancelSupplyOrdersActionsEnum
    cancellationReason?: keyof typeof SupplyOrderStatusCancelledReasonEnum
    cancellationDescription?: string
  }) => {
    const response = await mutateAsync({
      uuid: data?.data?.uuid,
      status: 'cancelled',
      action: values?.action,
      cancellationReason: values?.cancellationReason,
      cancellationDescription: values?.cancellationDescription,
    })

    if (!response.success) {
      return showToast({
        variant: 'error',
        title: t('errorMessageTitle'),
        content: t('errorMessageDescription'),
      })
    }

    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module, uuid],
    })
  }

  const tabs: TabsItemProps[] = [
    {
      title: t('tabGeneral'),
      isActive: params?.tab === 'general',
      onSelect: () => onChangeTab('general'),
    },
    {
      title: t('tabProducts'),
      isActive: params?.tab === 'products',
      onSelect: () => onChangeTab('products'),
    },
    {
      title: t('tabPayments'),
      isActive: params?.tab === 'payments',
      onSelect: () => onChangeTab('payments'),
    },
    {
      title: t('tabDocuments'),
      isActive: params?.tab === 'documents',
      onSelect: () => onChangeTab('documents'),
    },
    {
      title: t('tabComments'),
      isActive: params?.tab === 'comments',
      onSelect: () => onChangeTab('comments'),
    },
    {
      title: t('tabHistory'),
      isActive: params?.tab === 'history',
      onSelect: () => onChangeTab('history'),
    },
  ]

  const tabsContent: Record<string, JSX.Element> = {
    general: <TabGeneral supplyOrder={data?.data} isLoading={isLoading} />,
    products: <TabProducts supplyOrder={data?.data} isLoading={isLoading} />,
    payments: (
      <TabPayments
        supplyOrder={data?.data}
        isLoading={isLoading}
        params={params}
        setParams={setParams}
      />
    ),
    documents: <TabDocuments supplyOrder={data?.data} isLoading={isLoading} />,
    comments: (
      <TabComments supplyOrderUuid={data?.data?.uuid} isLoading={isLoading} />
    ),
    history: <TabHistory supplyOrderUuid={data?.data?.uuid} />,
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <Text variant="text-small" className={styles.breadcrumb}>
          <Link
            to={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.fullPath()}
          >{`${t('breadcrumTitle')}`}</Link>
          {` / `}
          <span>{data?.data?.reference}</span>
        </Text>
        <div className={styles['header-content']}>
          <div>
            <div className={styles['sub-title']}>
              <Text variant="title-large">{data?.data?.reference}</Text>
              <SelectSupplyOrderStatus supplyOrder={data?.data} />
            </div>
            <Text
              variant="text-base"
              className={styles['sub-title-instructions']}
            >
              {t('subtitle')}
            </Text>
          </div>
          <div className={styles['header-actions']}>
            {data?.data?.status !== 'cancelled' && (
              <Button
                variant="danger-outline"
                model="pill"
                onClick={() => setIsOpenModalCancellation(true)}
                className={styles.button}
              >
                {t('cancelSupplyOrderButton')}
              </Button>
            )}
            <Button
              variant="primary-outline"
              model="pill"
              href={data?.data?.supplyQuotation?.purchaseOrderFile}
              target="_blank"
              className={styles.button}
            >
              {t('purchaseOrderFileButton')}
            </Button>
            <Button
              variant="primary-outline"
              model="pill"
              href={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.UPDATE.fullPath(
                uuid || '',
              )}
              className={styles.button}
            >
              <EditIcon />
            </Button>
          </div>
        </div>
      </header>

      <div>
        <Tabs items={tabs} isLoading={isLoading} />
      </div>

      {tabsContent[params?.tab || 'general']}

      <ModalSupplyOrderCancellationReason
        isOpen={isOpenModalCancellation}
        setIsOpen={setIsOpenModalCancellation}
        onConfirm={onCancelSupplyOrder}
      />
    </div>
  )
}
